/**
 * Préparation d'une carte de masquage (design system, EditeurDeMasquage) : zones dessinées à
 * la souris sur une image, ou cases d'un tableau grisées d'un clic (spécification §4).
 * Positions des zones en fraction de l'image (0 à 1), comme dans le contenu enregistré.
 */
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as EvenementPointeur,
} from 'react';
import type { CaseTableau, ContenuImage, ContenuTableau, Zone } from '../../cartes/carte-mere';
import { Bouton } from '../composants/Bouton';
import { CommandeSegmentee } from '../composants/CommandeSegmentee';
import { Icone } from '../composants/Icone';
import { MessageDeChamp } from '../composants/MessageDeChamp';
import { rendreEnLigneMarkdown } from '../contenu/ContenuDeCarte';
import { pluriel } from '../outils';
import { preparerImage } from './images';

const TAILLE_MINIMUM = 0.02;

export function nouvelIdentifiant(prefixe: string): string {
  return `${prefixe}${crypto.randomUUID().replace(/-/g, '').slice(0, 8)}`;
}

const borner = (v: number, min = 0, max = 1) => Math.min(max, Math.max(min, v));

function arrondir(zone: Zone): Zone {
  const r = (v: number) => Math.round(v * 10000) / 10000;
  const largeur = r(borner(zone.largeur, TAILLE_MINIMUM / 2));
  const hauteur = r(borner(zone.hauteur, TAILLE_MINIMUM / 2));
  return {
    id: zone.id,
    x: r(borner(zone.x, 0, 1 - largeur)),
    y: r(borner(zone.y, 0, 1 - hauteur)),
    largeur,
    hauteur,
  };
}

// ---------------------------------------------------------------------------------------
// Image
// ---------------------------------------------------------------------------------------

export interface EditeurDImageProps {
  contenu: ContenuImage;
  onChange: (contenu: ContenuImage) => void;
  /** Nombre de cartes générées avec le mode actuel. */
  cartesGenerees: number;
  envoyerImage: (image: Blob, extension: string) => Promise<string>;
  resoudreImage: (chemin: string) => Promise<string>;
  erreur?: string;
  onErreur: (message: string) => void;
  desactive?: boolean;
}

type Geste =
  | { type: 'tracer'; id: string; x: number; y: number }
  | { type: 'deplacer'; id: string; x: number; y: number; zone: Zone }
  | { type: 'redimensionner'; id: string; coin: string; zone: Zone };

export function EditeurDImage({
  contenu,
  onChange,
  cartesGenerees,
  envoyerImage,
  resoudreImage,
  erreur,
  onErreur,
  desactive = false,
}: EditeurDImageProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [choisie, setChoisie] = useState<string | null>(null);
  const [trace, setTrace] = useState<Zone | null>(null);
  const [survol, setSurvol] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const cadre = useRef<HTMLDivElement>(null);
  const saisie = useRef<HTMLInputElement>(null);
  const geste = useRef<Geste | null>(null);
  const zones = contenu.zones;
  const contenuCourant = useRef(contenu);
  contenuCourant.current = contenu;

  useEffect(() => {
    if (!contenu.image) {
      setSrc(null);
      return;
    }
    let actif = true;
    void resoudreImage(contenu.image)
      .then((adresse) => actif && setSrc(adresse))
      .catch(() => actif && onErreur('L’image de la carte est introuvable.'));
    return () => {
      actif = false;
    };
  }, [contenu.image, resoudreImage, onErreur]);

  async function importer(fichier: File | undefined) {
    if (!fichier) return;
    setEnvoi(true);
    try {
      const { image, extension } = await preparerImage(fichier);
      const chemin = await envoyerImage(image, extension);
      onChange({ ...contenuCourant.current, image: chemin });
    } catch (e) {
      onErreur(e instanceof Error ? e.message : 'L’image n’a pas pu être ajoutée.');
    } finally {
      setEnvoi(false);
    }
  }

  const position = (e: { clientX: number; clientY: number }) => {
    const r = cadre.current!.getBoundingClientRect();
    return { x: borner((e.clientX - r.left) / r.width), y: borner((e.clientY - r.top) / r.height) };
  };

  const remplacer = (id: string, zone: Zone) =>
    onChange({
      ...contenuCourant.current,
      zones: contenuCourant.current.zones.map((z) => (z.id === id ? arrondir(zone) : z)),
    });

  function appui(e: EvenementPointeur<HTMLDivElement>) {
    if (desactive || e.button !== 0) return;
    const cible = e.target as HTMLElement;
    const elementZone = cible.closest<HTMLElement>('.fc-editeur-de-masquage__zone');
    const p = position(e);
    cadre.current!.setPointerCapture(e.pointerId);
    if (elementZone?.dataset.id) {
      const zone = zones.find((z) => z.id === elementZone.dataset.id)!;
      setChoisie(zone.id);
      elementZone.focus({ preventScroll: true });
      const coin = cible.dataset.coin;
      geste.current = coin
        ? { type: 'redimensionner', id: zone.id, coin, zone }
        : { type: 'deplacer', id: zone.id, ...p, zone };
      return;
    }
    const id = nouvelIdentifiant('z');
    geste.current = { type: 'tracer', id, ...p };
    setTrace({ id, x: p.x, y: p.y, largeur: 0, hauteur: 0 });
  }

  function mouvement(e: EvenementPointeur<HTMLDivElement>) {
    const g = geste.current;
    if (!g) return;
    const p = position(e);
    if (g.type === 'tracer') {
      setTrace({
        id: g.id,
        x: Math.min(p.x, g.x),
        y: Math.min(p.y, g.y),
        largeur: Math.abs(p.x - g.x),
        hauteur: Math.abs(p.y - g.y),
      });
    } else if (g.type === 'deplacer') {
      remplacer(g.id, { ...g.zone, x: g.zone.x + p.x - g.x, y: g.zone.y + p.y - g.y });
    } else {
      const { zone, coin } = g;
      const droite = zone.x + zone.largeur;
      const bas = zone.y + zone.hauteur;
      const x1 = coin.includes('o') ? Math.min(p.x, droite - TAILLE_MINIMUM) : zone.x;
      const x2 = coin.includes('e') ? Math.max(p.x, zone.x + TAILLE_MINIMUM) : droite;
      const y1 = coin.includes('n') ? Math.min(p.y, bas - TAILLE_MINIMUM) : zone.y;
      const y2 = coin.includes('s') ? Math.max(p.y, zone.y + TAILLE_MINIMUM) : bas;
      remplacer(g.id, { id: zone.id, x: x1, y: y1, largeur: x2 - x1, hauteur: y2 - y1 });
    }
  }

  function relachement() {
    const g = geste.current;
    geste.current = null;
    if (g?.type === 'tracer' && trace) {
      setTrace(null);
      if (trace.largeur >= TAILLE_MINIMUM && trace.hauteur >= TAILLE_MINIMUM) {
        onChange({
          ...contenuCourant.current,
          zones: [...contenuCourant.current.zones, arrondir(trace)],
        });
        setChoisie(trace.id);
      }
    }
  }

  function clavierZone(e: KeyboardEvent<HTMLDivElement>, zone: Zone) {
    const pas = 0.01;
    const dx = e.key === 'ArrowRight' ? pas : e.key === 'ArrowLeft' ? -pas : 0;
    const dy = e.key === 'ArrowDown' ? pas : e.key === 'ArrowUp' ? -pas : 0;
    if (dx || dy) {
      e.preventDefault();
      if (e.shiftKey)
        remplacer(zone.id, { ...zone, largeur: zone.largeur + dx, hauteur: zone.hauteur + dy });
      else remplacer(zone.id, { ...zone, x: zone.x + dx, y: zone.y + dy });
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      supprimer(zone.id);
    }
  }

  function supprimer(id: string | null) {
    if (!id) return;
    const restantes = zones.filter((z) => z.id !== id);
    onChange({ ...contenu, zones: restantes });
    setChoisie(restantes.at(-1)?.id ?? null);
  }

  const compte = `${pluriel(zones.length, 'zone')}, ${pluriel(cartesGenerees, 'carte')}`;

  if (!contenu.image) {
    return (
      <div className="fc-editeur-de-masquage">
        <div
          className="fc-editeur-de-masquage__depot"
          data-survol={survol ? '' : undefined}
          onDragOver={(e) => {
            e.preventDefault();
            setSurvol(true);
          }}
          onDragLeave={() => setSurvol(false)}
          onDrop={(e) => {
            e.preventDefault();
            setSurvol(false);
            void importer([...e.dataTransfer.files].find((f) => f.type.startsWith('image/')));
          }}
          onPaste={(e) =>
            void importer([...e.clipboardData.files].find((f) => f.type.startsWith('image/')))
          }
          tabIndex={0}
        >
          <Icone nom="image" />
          <p className="fc-editeur-de-masquage__invite">Déposez une image ici ou collez-la</p>
          <p className="fc-editeur-de-masquage__aide">PNG, JPEG, WebP, GIF ou AVIF.</p>
          <Bouton taille="compacte" occupe={envoi} onClick={() => saisie.current?.click()}>
            Choisir une image
          </Bouton>
        </div>
        {erreur && (
          <MessageDeChamp id="masquage-erreur" ton="erreur">
            {erreur}
          </MessageDeChamp>
        )}
        <input
          ref={saisie}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
          className="fc-sr"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => {
            void importer(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </div>
    );
  }

  const enPourcents = (z: Zone) => ({
    left: `${z.x * 100}%`,
    top: `${z.y * 100}%`,
    width: `${z.largeur * 100}%`,
    height: `${z.hauteur * 100}%`,
  });

  return (
    <div className="fc-editeur-de-masquage">
      <div className="fc-editeur-de-masquage__outils" role="toolbar" aria-label="Zones">
        <Bouton
          variante="discret"
          taille="compacte"
          icone="plus"
          desactive={desactive}
          onClick={() => {
            const zone = arrondir({
              id: nouvelIdentifiant('z'),
              x: 0.4,
              y: 0.45,
              largeur: 0.2,
              hauteur: 0.1,
            });
            onChange({ ...contenu, zones: [...zones, zone] });
            setChoisie(zone.id);
          }}
        >
          Ajouter une zone
        </Bouton>
        <Bouton
          variante="discret"
          taille="compacte"
          iconeSeule
          icone="trash-2"
          etiquette="Supprimer la zone"
          aria-keyshortcuts="Delete"
          desactive={desactive || choisie === null}
          onClick={() => supprimer(choisie)}
        />
        <Bouton
          variante="discret"
          taille="compacte"
          icone="image"
          occupe={envoi}
          desactive={desactive}
          onClick={() => saisie.current?.click()}
        >
          Remplacer l’image
        </Bouton>
        <p className="fc-editeur-de-masquage__compte" aria-live="polite">
          {compte}
        </p>
      </div>
      <div
        ref={cadre}
        className="fc-editeur-de-masquage__cadre"
        role="listbox"
        aria-label="Zones de l’image"
        aria-describedby="masquage-aide"
        onPointerDown={appui}
        onPointerMove={mouvement}
        onPointerUp={relachement}
        onPointerCancel={relachement}
      >
        {src ? (
          <img
            className="fc-editeur-de-masquage__image"
            src={src}
            alt="Image de la carte"
            draggable={false}
          />
        ) : (
          <div className="fc-editeur-de-masquage__image" style={{ aspectRatio: '4 / 3' }} />
        )}
        {zones.map((zone, i) => (
          <div
            key={zone.id}
            data-id={zone.id}
            className="fc-editeur-de-masquage__zone"
            role="option"
            aria-selected={zone.id === choisie}
            tabIndex={zone.id === (choisie ?? zones[0]?.id) ? 0 : -1}
            aria-label={`Zone ${i + 1}`}
            style={enPourcents(zone)}
            onFocus={() => setChoisie(zone.id)}
            onKeyDown={(e) => clavierZone(e, zone)}
          >
            <span className="fc-editeur-de-masquage__numero" aria-hidden="true">
              {i + 1}
            </span>
            {['no', 'ne', 'so', 'se'].map((coin) => (
              <span
                key={coin}
                className="fc-editeur-de-masquage__poignee"
                data-coin={coin}
                aria-hidden="true"
              />
            ))}
          </div>
        ))}
        {trace && (
          <div
            className="fc-editeur-de-masquage__zone"
            data-trace
            aria-hidden="true"
            style={enPourcents(trace)}
          >
            <span className="fc-editeur-de-masquage__numero">{zones.length + 1}</span>
          </div>
        )}
      </div>
      <p className="fc-editeur-de-masquage__aide" id="masquage-aide">
        Faites glisser la souris sur l’image pour dessiner une zone. Flèches&nbsp;: déplacer la zone
        choisie. ⇧ et flèches&nbsp;: la redimensionner. Suppr&nbsp;: la retirer.
      </p>
      {erreur && (
        <MessageDeChamp id="masquage-erreur" ton="erreur">
          {erreur}
        </MessageDeChamp>
      )}
      <input
        ref={saisie}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
        className="fc-sr"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          void importer(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// Tableau
// ---------------------------------------------------------------------------------------

/** Tableau de départ : une ligne d'en-têtes et deux lignes, deux colonnes. */
export function tableauVide(): ContenuTableau {
  const ligne = () =>
    [0, 1].map((): CaseTableau => ({ id: nouvelIdentifiant('c'), contenu: '', grisee: false }));
  return { lignes: [ligne(), ligne(), ligne()] };
}

export interface EditeurDeTableauProps {
  contenu: ContenuTableau;
  onChange: (contenu: ContenuTableau) => void;
  cartesGenerees: number;
  erreur?: string;
  desactive?: boolean;
}

export function EditeurDeTableau({
  contenu,
  onChange,
  cartesGenerees,
  erreur,
  desactive = false,
}: EditeurDeTableauProps) {
  const [action, setAction] = useState<'ecrire' | 'griser'>('ecrire');
  const lignes = contenu.lignes;
  const colonnes = Math.max(1, ...lignes.map((l) => l.length));
  const grisees = lignes
    .slice(1)
    .flat()
    .filter((c) => c.grisee);
  const numeros = new Map(grisees.map((c, i) => [c.id, i + 1]));

  const modifierCase = (id: string, modification: Partial<CaseTableau>) =>
    onChange({
      lignes: lignes.map((l) => l.map((c) => (c.id === id ? { ...c, ...modification } : c))),
    });

  const ajouterLigne = () =>
    onChange({
      lignes: [
        ...lignes,
        Array.from({ length: colonnes }, () => ({
          id: nouvelIdentifiant('c'),
          contenu: '',
          grisee: false,
        })),
      ],
    });
  const ajouterColonne = () =>
    onChange({
      lignes: lignes.map((l) => [...l, { id: nouvelIdentifiant('c'), contenu: '', grisee: false }]),
    });
  const retirerLigne = (i: number) => onChange({ lignes: lignes.filter((_, j) => j !== i) });
  const retirerColonne = (k: number) =>
    onChange({ lignes: lignes.map((l) => l.filter((_, j) => j !== k)) });

  function clavierGrille(e: KeyboardEvent<HTMLTableElement>) {
    const cellule = (e.target as HTMLElement).closest<HTMLElement>('[data-ligne]');
    if (!cellule) return;
    const i = Number(cellule.dataset.ligne);
    const k = Number(cellule.dataset.colonne);
    const aller = (li: number, co: number) =>
      (
        e.currentTarget.querySelector(
          `[data-ligne="${li}"][data-colonne="${co}"]`,
        ) as HTMLElement | null
      )?.focus();
    if (action === 'griser' && (e.key === ' ' || e.key === 'Enter') && i > 0) {
      e.preventDefault();
      const c = lignes[i]![k]!;
      modifierCase(c.id, { grisee: !c.grisee });
    } else if (action === 'griser' && e.key.startsWith('Arrow')) {
      e.preventDefault();
      if (e.key === 'ArrowRight') aller(i, Math.min(colonnes - 1, k + 1));
      if (e.key === 'ArrowLeft') aller(i, Math.max(0, k - 1));
      if (e.key === 'ArrowDown') aller(Math.min(lignes.length - 1, i + 1), k);
      if (e.key === 'ArrowUp') aller(Math.max(1, i - 1), k);
    }
  }

  return (
    <div
      className={`fc-editeur-de-masquage${action === 'griser' ? ' fc-editeur-de-masquage--griser' : ''}`}
    >
      <div className="fc-editeur-de-masquage__outils">
        <CommandeSegmentee
          taille="petite"
          etiquette="Action d’un clic sur une case"
          options={[
            { valeur: 'ecrire', libelle: 'Écrire', icone: 'pencil' },
            { valeur: 'griser', libelle: 'Griser les cases', icone: 'square-dashed-mouse-pointer' },
          ]}
          valeur={action}
          onChoisir={setAction}
          desactivee={desactive}
        />
        <Bouton
          variante="discret"
          taille="compacte"
          icone="plus"
          desactive={desactive}
          onClick={ajouterLigne}
        >
          Ligne
        </Bouton>
        <Bouton
          variante="discret"
          taille="compacte"
          icone="plus"
          desactive={desactive}
          onClick={ajouterColonne}
        >
          Colonne
        </Bouton>
        <p className="fc-editeur-de-masquage__compte" aria-live="polite">
          {`${pluriel(grisees.length, 'case grisée', 'cases grisées')}, ${pluriel(cartesGenerees, 'carte')}`}
        </p>
      </div>
      <table
        className="fc-editeur-de-masquage__table"
        role={action === 'griser' ? 'grid' : undefined}
        aria-label={
          action === 'griser'
            ? 'Tableau de la carte : cliquez une case pour la griser'
            : 'Tableau de la carte'
        }
        onKeyDown={clavierGrille}
      >
        <thead>
          <tr>
            {lignes[0]?.map((c, k) => (
              <th key={c.id} scope="col">
                {action === 'ecrire' ? (
                  <input
                    className="fc-editeur-de-masquage__saisie"
                    value={c.contenu}
                    placeholder={`En-tête ${k + 1}`}
                    aria-label={`En-tête de la colonne ${k + 1}`}
                    disabled={desactive}
                    onChange={(e) => modifierCase(c.id, { contenu: e.target.value })}
                  />
                ) : (
                  rendreEnLigneMarkdown(c.contenu)
                )}
                {action === 'ecrire' && colonnes > 1 && (
                  <button
                    type="button"
                    className="fc-editeur-de-masquage__retirer"
                    aria-label={`Retirer la colonne ${k + 1}`}
                    title="Retirer la colonne"
                    onClick={() => retirerColonne(k)}
                  >
                    <Icone nom="x" taille="petite" />
                  </button>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lignes.slice(1).map((ligne, j) => {
            const i = j + 1;
            return (
              <tr key={ligne[0]?.id ?? i}>
                {ligne.map((c, k) =>
                  action === 'ecrire' ? (
                    <td
                      key={c.id}
                      aria-selected={undefined}
                      data-grisee={c.grisee ? '' : undefined}
                    >
                      <input
                        className="fc-editeur-de-masquage__saisie"
                        value={c.contenu}
                        aria-label={`Ligne ${i}, colonne ${k + 1}`}
                        disabled={desactive}
                        onChange={(e) => modifierCase(c.id, { contenu: e.target.value })}
                      />
                      {k === ligne.length - 1 && lignes.length > 2 && (
                        <button
                          type="button"
                          className="fc-editeur-de-masquage__retirer"
                          aria-label={`Retirer la ligne ${i}`}
                          title="Retirer la ligne"
                          onClick={() => retirerLigne(i)}
                        >
                          <Icone nom="x" taille="petite" />
                        </button>
                      )}
                    </td>
                  ) : (
                    <td
                      key={c.id}
                      role="gridcell"
                      tabIndex={i === 1 && k === 0 ? 0 : -1}
                      aria-selected={c.grisee}
                      data-ligne={i}
                      data-colonne={k}
                      onClick={() => !desactive && modifierCase(c.id, { grisee: !c.grisee })}
                    >
                      {rendreEnLigneMarkdown(c.contenu)}
                      {c.grisee && (
                        <span className="fc-editeur-de-masquage__numero" aria-hidden="true">
                          {numeros.get(c.id)}
                        </span>
                      )}
                    </td>
                  ),
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {erreur && (
        <MessageDeChamp id="masquage-erreur" ton="erreur">
          {erreur}
        </MessageDeChamp>
      )}
    </div>
  );
}
